import { c as _c } from "react/compiler-runtime";
import { getOrgArtifactsOptions } from '@onefootprint/fetch-dashboard';
import { IcoBook16, IcoBuilding16, IcoChevronRight16, IcoClose16, IcoFileText16, IcoPaint16, IcoRobot16, IcoSearch16, IcoShield16, IcoSquareFrame16, IcoUser16 } from '@onefootprint/icons';
import type { ArtifactMimeType } from '@onefootprint/request-types/dashboard';
import type { ResourceContext } from '@onefootprint/request-types/percy';
import { SearchInput, Tooltip } from '@onefootprint/ui';
import { useQueries } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { type MouseEvent, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMobileLayout from '@/hooks/use-mobile-layout';
import getArtifactIcon from '@/utils/get-artifact-icon';
import { presentResource, RESOURCE_GROUP_ORDER, type ResourceGroupKey, type ResourcePresentation, resourceGroup } from '../../utils/resource-presentation';
import { flashResourceRow, scrollToResourceMarker, subscribeRevealResource } from '../../utils/reveal-resource';
import { useAgentNames, useEntityNames, usePlaybookNames } from '../resource-name';
import type { ChatResource } from './chat-resources-context';

// Fallback mime type guessed from a name's extension, used only while the artifact fetch (which has
// the real mime type) is in flight — the socket frame doesn't carry it, and prod names often lack an
// extension, so this is best-effort.
const EXTENSION_MIME: Record<string, ArtifactMimeType> = {
  pdf: 'application/pdf',
  md: 'text/markdown',
  markdown: 'text/markdown',
  json: 'application/json',
  jsonl: 'application/jsonl',
  csv: 'text/csv',
  txt: 'text/plain',
  html: 'text/html',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif'
};
const extensionMime = (name: string): ArtifactMimeType | undefined => {
  const ext = name.includes('.') ? name.split('.').pop()?.toLowerCase() ?? '' : '';
  return EXTENSION_MIME[ext];
};

// Document icon: prefer the fetched mime type, fall back to the name's extension while the fetch is in
// flight, then a generic file icon.
const documentIcon = (mime: ArtifactMimeType | undefined, name: string): ReactNode => {
  const resolved = mime ?? extensionMime(name);
  const Icon = resolved ? getArtifactIcon(resolved) : IcoFileText16;
  return <Icon />;
};

// The socket frame has no mime type, so fetch the artifact records (same source as the investigation
// page's Artifacts panel) for every investigation the chat references and map artifactId → mimeType.
// React Query dedups/caches, so this shares with the investigation page.
const useArtifactMimeTypes = items => {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] !== items) {
    const investigationIds = [...new Set(items.flatMap(_temp))];
    t0 = useQueries;
    t1 = {
      queries: investigationIds.map(_temp2)
    };
    $[0] = items;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const queries = t0(t1);
  let mimeByArtifactId;
  if ($[3] !== queries) {
    mimeByArtifactId = new Map();
    for (const q of queries) {
      for (const artifact of q.data?.data ?? []) {
        mimeByArtifactId.set(artifact.id, artifact.mimeType);
      }
    }
    $[3] = queries;
    $[4] = mimeByArtifactId;
  } else {
    mimeByArtifactId = $[4];
  }
  return mimeByArtifactId;
};

// One resource, resolved for display: its name (or id fallback), per-kind icon, and any nested
// children (documents/artifacts nest under the investigation they belong to).
type PresentedItem = {
  key: string;
  resource: ResourceContext;
  name: string;
  icon: ReactNode;
  children?: PresentedItem[];
};
export type ContextExplorerProps = {
  /** Every resource the chat created or referenced, in creation order (from the chat-resources context). */
  items: ChatResource[];
  /** The resource the current route is on — highlighted and its folder opened. */
  activeId?: string | null;
  /** Render folders + investigation nodes expanded on first mount (e.g. for snapshots). */
  defaultOpen?: boolean;
  onClose?: () => void;
  /** A row's "Open" action fired. The caller composes the URL and navigates;
   * `event` carries the modifier keys (cmd/ctrl opens a new tab). Also the fallback when a
   * row's resource has no marker in the thread to scroll to. */
  onResourceSelected?: (resource: ResourceContext, event: MouseEvent) => void;
};

// Phone sizes match the widget header this panel slides over, so the two read as one bar.
const iconButtonClass = 'flex items-center justify-center size-11 md:size-7 rounded-(--icon-button-border-radius) hover:bg-hover-control cursor-pointer focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary';
const matchesQuery = (text: string, query: string) => text.toLowerCase().includes(query.trim().toLowerCase());

// Every key at or under these nodes — used to auto-open a folder / investigation when the active or
// just-revealed resource lives somewhere inside it.
const descendantKeys = (nodes: PresentedItem[]): string[] => nodes.flatMap(n => [n.key, ...descendantKeys(n.children ?? [])]);
type RowHandlers = {
  activeId?: string | null;
  reveal?: {
    key: string;
    nonce: number;
  } | null;
  openLabel: string;
  revealLabel: string;
  onActivate?: (resource: ResourceContext, key: string, event: MouseEvent) => void;
  onOpen?: (resource: ResourceContext, event: MouseEvent) => void;
  forceOpen?: boolean;
  defaultOpen?: boolean;
};

// A single hoverable row (Notion-style). Clicking the name expands the document list (an
// investigation that owns documents) or opens the resource's own page (everything else). A frame
// button sits next to the name to locate it in the chat thread; a far-right chevron — shown only
// when it owns documents — is the expand affordance. Documents nest below off a left guide line.
const ResourceRow = t0 => {
  const $ = _c(73);
  const {
    item,
    activeId,
    reveal,
    openLabel,
    revealLabel,
    onActivate,
    onOpen,
    forceOpen,
    defaultOpen
  } = t0;
  let children;
  let hasActiveChild;
  let hasChildren;
  let t1;
  if ($[0] !== activeId || $[1] !== item.children || $[2] !== reveal) {
    children = item.children ?? [];
    hasChildren = children.length > 0;
    const childKeys = descendantKeys(children);
    hasActiveChild = !!activeId && childKeys.includes(activeId);
    t1 = !!reveal && childKeys.includes(reveal.key);
    $[0] = activeId;
    $[1] = item.children;
    $[2] = reveal;
    $[3] = children;
    $[4] = hasActiveChild;
    $[5] = hasChildren;
    $[6] = t1;
  } else {
    children = $[3];
    hasActiveChild = $[4];
    hasChildren = $[5];
    t1 = $[6];
  }
  const hasRevealChild = t1;
  const [userOpen, setUserOpen] = useState(defaultOpen ?? false);
  let t2;
  let t3;
  if ($[7] !== hasActiveChild) {
    t2 = () => {
      if (hasActiveChild) {
        setUserOpen(true);
      }
    };
    t3 = [hasActiveChild];
    $[7] = hasActiveChild;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== hasRevealChild) {
    t4 = () => {
      if (hasRevealChild) {
        setUserOpen(true);
      }
    };
    $[10] = hasRevealChild;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = reveal?.nonce;
  let t6;
  if ($[12] !== hasRevealChild || $[13] !== t5) {
    t6 = [hasRevealChild, t5];
    $[12] = hasRevealChild;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  useEffect(t4, t6);
  const open = forceOpen || userOpen;
  const isActive = item.key === activeId;
  const showIcon = item.resource.kind === "artifact";
  const t7 = item.key;
  const t8 = isActive ? "bg-senary/60" : "hover:bg-hover";
  let t9;
  if ($[15] !== t8) {
    t9 = cx("group/row relative flex items-center gap-1 h-9 md:h-7 px-1.5 rounded-default transition-colors", t8);
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = isActive ? "true" : undefined;
  const t11 = hasChildren ? open : undefined;
  const t12 = hasChildren ? item.name : `${openLabel}: ${item.name}`;
  let t13;
  if ($[17] !== hasChildren || $[18] !== item.resource || $[19] !== onOpen) {
    t13 = hasChildren ? () => setUserOpen(_temp3) : event => onOpen?.(item.resource, event);
    $[17] = hasChildren;
    $[18] = item.resource;
    $[19] = onOpen;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t12 || $[24] !== t13) {
    t14 = <button type="button" aria-current={t10} aria-expanded={t11} aria-label={t12} onClick={t13} className="absolute inset-0 rounded-default outline-none cursor-pointer focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-inset" />;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = isActive ? "text-primary text-label-3" : "text-tertiary group-hover/row:text-primary text-body-3";
  let t16;
  if ($[26] !== t15) {
    t16 = cx("relative min-w-0 flex items-center gap-2 pointer-events-none", t15);
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== item.icon || $[29] !== showIcon) {
    t17 = showIcon && <span className="shrink-0">{item.icon}</span>;
    $[28] = item.icon;
    $[29] = showIcon;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== item.name) {
    t18 = <span className="truncate">{item.name}</span>;
    $[31] = item.name;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t16 || $[34] !== t17 || $[35] !== t18) {
    t19 = <span className={t16}>{t17}{t18}</span>;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const t20 = `${revealLabel}: ${item.name}`;
  let t21;
  if ($[37] !== item.key || $[38] !== item.resource || $[39] !== onActivate) {
    t21 = event_0 => onActivate?.(item.resource, item.key, event_0);
    $[37] = item.key;
    $[38] = item.resource;
    $[39] = onActivate;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <IcoSquareFrame16 />;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t20 || $[43] !== t21) {
    t23 = <button type="button" aria-label={t20} onClick={t21} className="relative z-10 shrink-0 size-7 md:size-5 flex items-center justify-center rounded-(--icon-button-border-radius) md:rounded-(--icon-button-border-radius-tiny) text-tertiary hover:text-primary hover:bg-hover-control opacity-0 max-md:opacity-100 transition outline-none cursor-pointer group-hover/row:opacity-100 focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-inset">{t22}</button>;
    $[42] = t20;
    $[43] = t21;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== revealLabel || $[46] !== t23) {
    t24 = <Tooltip content={revealLabel}>{t23}</Tooltip>;
    $[45] = revealLabel;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== hasChildren || $[49] !== open) {
    t25 = hasChildren && <span className="relative ml-auto shrink-0 size-5 flex items-center justify-center pointer-events-none"><IcoChevronRight16 className={cx("transition-transform duration-200 ease-in-out", open && "rotate-90")} color="tertiary" /></span>;
    $[48] = hasChildren;
    $[49] = open;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== item.key || $[52] !== t14 || $[53] !== t19 || $[54] !== t24 || $[55] !== t25 || $[56] !== t9) {
    t26 = <div data-resource-row={t7} className={t9}>{t14}{t19}{t24}{t25}</div>;
    $[51] = item.key;
    $[52] = t14;
    $[53] = t19;
    $[54] = t24;
    $[55] = t25;
    $[56] = t9;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== activeId || $[59] !== children || $[60] !== defaultOpen || $[61] !== forceOpen || $[62] !== hasChildren || $[63] !== onActivate || $[64] !== onOpen || $[65] !== open || $[66] !== openLabel || $[67] !== reveal || $[68] !== revealLabel) {
    t27 = hasChildren && open && <ul className="mt-0.5 mb-0 ml-2.5 pl-3.5 flex flex-col gap-0.5 list-none border-l border-tertiary">{children.map(child => <ResourceRow key={child.key} item={child} activeId={activeId} reveal={reveal} openLabel={openLabel} revealLabel={revealLabel} onActivate={onActivate} onOpen={onOpen} forceOpen={forceOpen} defaultOpen={defaultOpen} />)}</ul>;
    $[58] = activeId;
    $[59] = children;
    $[60] = defaultOpen;
    $[61] = forceOpen;
    $[62] = hasChildren;
    $[63] = onActivate;
    $[64] = onOpen;
    $[65] = open;
    $[66] = openLabel;
    $[67] = reveal;
    $[68] = revealLabel;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== t26 || $[71] !== t27) {
    t28 = <li className="m-0 p-0 flex flex-col">{t26}{t27}</li>;
    $[70] = t26;
    $[71] = t27;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  return t28;
};

// The label a resource shows in the explorer. A title-less investigation is named after its subject
// entity when one resolves (`entityNames` is keyed by fp_id, not the resource key); otherwise every
// kind falls back to its id, the handle the dashboard identifies resources by everywhere else.
const displayName = (resource: ResourceContext, key: string, presentation: ResourcePresentation, entityNames: Map<string, string>): string => {
  if (resource.kind === 'investigation') {
    const subject = resource.fp_id ? entityNames.get(resource.fp_id) : undefined;
    return presentation.name ?? subject ?? key;
  }
  return entityNames.get(key) ?? presentation.name ?? key;
};

// A collapsible top-level folder grouping resources by kind. Only the chevron toggles it (the label
// is plain text, matching the artifacts sidebar). `forceOpen` pins it open while searching.
const FolderGroup = t0 => {
  const $ = _c(44);
  const {
    label,
    icon,
    items,
    forceOpen,
    defaultOpen,
    activeId,
    reveal,
    openLabel,
    revealLabel,
    onActivate,
    onOpen
  } = t0;
  let hasActive;
  let t1;
  if ($[0] !== activeId || $[1] !== items || $[2] !== reveal) {
    const keys = descendantKeys(items);
    hasActive = !!activeId && keys.includes(activeId);
    t1 = !!reveal && keys.includes(reveal.key);
    $[0] = activeId;
    $[1] = items;
    $[2] = reveal;
    $[3] = hasActive;
    $[4] = t1;
  } else {
    hasActive = $[3];
    t1 = $[4];
  }
  const hasReveal = t1;
  const [userOpen, setUserOpen] = useState(defaultOpen ?? hasActive);
  let t2;
  let t3;
  if ($[5] !== hasActive) {
    t2 = () => {
      if (hasActive) {
        setUserOpen(true);
      }
    };
    t3 = [hasActive];
    $[5] = hasActive;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== hasReveal) {
    t4 = () => {
      if (hasReveal) {
        setUserOpen(true);
      }
    };
    $[8] = hasReveal;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = reveal?.nonce;
  let t6;
  if ($[10] !== hasReveal || $[11] !== t5) {
    t6 = [hasReveal, t5];
    $[10] = hasReveal;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useEffect(t4, t6);
  const open = forceOpen || userOpen;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setUserOpen(_temp4);
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== icon) {
    t8 = <span className="shrink-0">{icon}</span>;
    $[14] = icon;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== items.length) {
    t9 = <span className="tabular-nums">({items.length})</span>;
    $[16] = items.length;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== label || $[19] !== t9) {
    t10 = <span className="flex-1 min-w-0 text-label-3 text-tertiary truncate">{label} {t9}</span>;
    $[18] = label;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = open && "rotate-90";
  let t12;
  if ($[21] !== t11) {
    t12 = cx("transition-transform duration-200 ease-in-out", t11);
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12) {
    t13 = <span className="shrink-0 size-5 flex items-center justify-center"><IcoChevronRight16 className={t12} color="tertiary" /></span>;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== open || $[26] !== t10 || $[27] !== t13 || $[28] !== t8) {
    t14 = <button type="button" aria-expanded={open} onClick={t7} className="w-full flex items-center gap-2 h-9 md:h-7 px-1.5 rounded-default text-left cursor-pointer outline-none transition-colors hover:bg-hover focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-inset">{t8}{t10}{t13}</button>;
    $[25] = open;
    $[26] = t10;
    $[27] = t13;
    $[28] = t8;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== activeId || $[31] !== defaultOpen || $[32] !== forceOpen || $[33] !== items || $[34] !== onActivate || $[35] !== onOpen || $[36] !== open || $[37] !== openLabel || $[38] !== reveal || $[39] !== revealLabel) {
    t15 = open && <ul className="mt-1 mb-0 ml-2.5 pl-3.5 flex flex-col gap-0.5 list-none border-l border-tertiary">{items.map(item => <ResourceRow key={item.key} item={item} activeId={activeId} reveal={reveal} openLabel={openLabel} revealLabel={revealLabel} onActivate={onActivate} onOpen={onOpen} forceOpen={forceOpen} defaultOpen={defaultOpen} />)}</ul>;
    $[30] = activeId;
    $[31] = defaultOpen;
    $[32] = forceOpen;
    $[33] = items;
    $[34] = onActivate;
    $[35] = onOpen;
    $[36] = open;
    $[37] = openLabel;
    $[38] = reveal;
    $[39] = revealLabel;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t14 || $[42] !== t15) {
    t16 = <div className="flex flex-col">{t14}{t15}</div>;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
};
const ContextExplorer = t0 => {
  const $ = _c(40);
  const {
    items,
    activeId,
    defaultOpen,
    onClose,
    onResourceSelected
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  const isMobile = useMobileLayout();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  let t1;
  if ($[0] !== t) {
    t1 = t(_temp5);
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const title = t1;
  const [reveal, setReveal] = useState(null);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => subscribeRevealResource(key => setReveal(prev => ({
      key,
      nonce: (prev?.nonce ?? 0) + 1
    })));
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== reveal) {
    t4 = () => {
      if (!reveal) {
        return;
      }
      const flashTimer = setTimeout(() => flashResourceRow(reveal.key), 350);
      return () => clearTimeout(flashTimer);
    };
    t5 = [reveal];
    $[4] = reveal;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setSearchOpen(false);
      setQuery("");
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const closeSearch = t6;
  let t7;
  if ($[8] !== isMobile || $[9] !== onClose || $[10] !== onResourceSelected) {
    t7 = (resource, key_0, event) => {
      if (scrollToResourceMarker(key_0)) {
        if (isMobile) {
          onClose?.();
        }
        return;
      }
      onResourceSelected?.(resource, event);
    };
    $[8] = isMobile;
    $[9] = onClose;
    $[10] = onResourceSelected;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handleActivate = t7;
  let t8;
  if ($[12] !== items) {
    t8 = items.flatMap(_temp6);
    $[12] = items;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const entityNames = useEntityNames(t8);
  let t9;
  if ($[14] !== items) {
    t9 = items.flatMap(_temp7);
    $[14] = items;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const agentNames = useAgentNames(t9);
  let t10;
  if ($[16] !== items) {
    t10 = items.flatMap(_temp8);
    $[16] = items;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const playbookNames = usePlaybookNames(t10);
  const nameById = new Map([...entityNames, ...agentNames, ...playbookNames]);
  const mimeByArtifactId = useArtifactMimeTypes(items);
  const present = (key_1, resource_3) => {
    const presentation = presentResource(resource_3, t);
    if (!presentation) {
      return null;
    }
    const name = displayName(resource_3, key_1, presentation, nameById);
    const icon = resource_3.kind === "artifact" ? documentIcon(mimeByArtifactId.get(resource_3.id), name) : presentation.icon;
    return {
      key: key_1,
      resource: resource_3,
      name,
      icon
    };
  };
  const groups = new Map();
  const investigationById = new Map();
  const pendingArtifacts = [];
  const addToGroup = (group, item) => {
    const bucket = groups.get(group) ?? [];
    bucket.push(item);
    groups.set(group, bucket);
  };
  for (const {
    key: key_2,
    resource: resource_4
  } of items) {
    const group_0 = resourceGroup(resource_4);
    if (!group_0) {
      continue;
    }
    const item_0 = present(key_2, resource_4);
    if (!item_0) {
      continue;
    }
    if (resource_4.kind === "artifact") {
      pendingArtifacts.push({
        item: item_0,
        investigationId: resource_4.investigation_id ?? null
      });
      continue;
    }
    addToGroup(group_0, item_0);
    if (resource_4.kind === "investigation") {
      investigationById.set(resource_4.id, item_0);
    }
  }
  for (const {
    item: item_1,
    investigationId
  } of pendingArtifacts) {
    const parent = investigationId ? investigationById.get(investigationId) : undefined;
    if (parent) {
      parent.children = [...(parent.children ?? []), item_1];
    }
  }
  const q = query.trim();
  const filterNodes = nodes => {
    if (!q) {
      return nodes;
    }
    const out = [];
    for (const node of nodes) {
      if (matchesQuery(node.name, q)) {
        out.push(node);
        continue;
      }
      const matchedChildren = (node.children ?? []).filter(c => matchesQuery(c.name, q));
      if (matchedChildren.length) {
        out.push({
          ...node,
          children: matchedChildren
        });
      }
    }
    return out;
  };
  const presented = new Map();
  for (const [group_1, nodes_0] of groups) {
    const filtered = filterNodes(nodes_0);
    if (filtered.length) {
      presented.set(group_1, filtered);
    }
  }
  let t11;
  if ($[18] !== t) {
    t11 = group_2 => {
      switch (group_2) {
        case "investigation":
          {
            return t(_temp9);
          }
        case "agent":
          {
            return t(_temp10);
          }
        case "playbook":
          {
            return t(_temp11);
          }
        case "person":
          {
            return t(_temp12);
          }
        case "business":
          {
            return t(_temp13);
          }
        case "onboarding":
          {
            return t(_temp14);
          }
        case "artifact":
          {
            return t(_temp15);
          }
        case "appearance":
          {
            return t(_temp16);
          }
      }
    };
    $[18] = t;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const groupLabel = t11;
  const groupIcon = _temp17;
  const isSearching = q.length > 0;
  const visibleGroups = (Object.keys(RESOURCE_GROUP_ORDER) as ResourceGroupKey[]).filter(group_4 => (presented.get(group_4)?.length ?? 0) > 0).sort(_temp18);
  const t12 = "complementary";
  const t13 = title;
  const t14 = "flex flex-col h-full w-full bg-primary overflow-hidden";
  let t15;
  if ($[20] !== onClose || $[21] !== query || $[22] !== searchOpen || $[23] !== t || $[24] !== title) {
    t15 = searchOpen ? <div className="flex items-center gap-1 border-b border-tertiary px-2 md:px-3 h-12 md:h-11 shrink-0"><div className="flex-1 min-w-0"><SearchInput size="compact" placeholder={t(_temp19)} clearButtonAriaLabel={t(_temp20)} value={query} onChangeText={setQuery} autoFocus={true} onKeyDown={e => {
          if (e.key === "Escape") {
            closeSearch();
          }
        }} /></div><button type="button" aria-label={t(_temp21)} className={iconButtonClass} onClick={closeSearch}><IcoClose16 /></button></div> : <div className="flex items-center gap-1 border-b border-tertiary px-2 md:px-3 h-12 md:h-11 shrink-0"><span className="flex-1 min-w-0 text-label-3 font-medium text-primary truncate ml-1">{title}</span><button type="button" aria-label={t(_temp22)} className={iconButtonClass} onClick={() => setSearchOpen(true)}><IcoSearch16 /></button><button type="button" aria-label={t(_temp23)} className={iconButtonClass} onClick={onClose}><IcoClose16 /></button></div>;
    $[20] = onClose;
    $[21] = query;
    $[22] = searchOpen;
    $[23] = t;
    $[24] = title;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const t16 = "flex-1 min-h-0 overflow-y-auto px-2 py-2 flex flex-col gap-1";
  const t17 = visibleGroups.map(group_5 => <FolderGroup key={group_5} label={groupLabel(group_5)} icon={groupIcon(group_5)} items={presented.get(group_5) ?? []} forceOpen={isSearching} defaultOpen={defaultOpen} activeId={activeId} reveal={reveal} openLabel={t(_temp24)} revealLabel={t(_temp25)} onActivate={handleActivate} onOpen={onResourceSelected} />);
  let t18;
  if ($[26] !== isSearching || $[27] !== t || $[28] !== visibleGroups.length) {
    t18 = visibleGroups.length === 0 && <p className="text-body-3 text-tertiary px-2 py-2">{isSearching ? t(_temp26) : t(_temp27)}</p>;
    $[26] = isSearching;
    $[27] = t;
    $[28] = visibleGroups.length;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t16 || $[31] !== t17 || $[32] !== t18) {
    t19 = <div className={t16}>{t17}{t18}</div>;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t12 || $[35] !== t13 || $[36] !== t14 || $[37] !== t15 || $[38] !== t19) {
    t20 = <div role={t12} aria-label={t13} className={t14}>{t15}{t19}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  return t20;
};
export default ContextExplorer;
function _temp(i) {
  return i.resource.kind === "artifact" && i.resource.investigation_id ? [i.resource.investigation_id] : [];
}
function _temp2(investigationId) {
  return {
    ...getOrgArtifactsOptions({
      query: {
        investigationId,
        pageSize: 100
      }
    }),
    enabled: !!investigationId
  };
}
function _temp3(o) {
  return !o;
}
function _temp4(o) {
  return !o;
}
function _temp5($) {
  return $.chat.contextExplorer.title;
}
function _temp6(t0) {
  const {
    resource: resource_0
  } = t0;
  if (resource_0.kind === "entity" && !resource_0.name) {
    return [resource_0.id];
  }
  if (resource_0.kind === "investigation" && !resource_0.name && resource_0.fp_id) {
    return [resource_0.fp_id];
  }
  return [];
}
function _temp7(t0) {
  const {
    resource: resource_1
  } = t0;
  return resource_1.kind === "agent" && !resource_1.name ? [resource_1.id] : [];
}
function _temp8(t0) {
  const {
    resource: resource_2
  } = t0;
  return resource_2.kind === "playbook" && !resource_2.name ? [resource_2.id] : [];
}
function _temp9($_7) {
  return $_7.chat.contextExplorer.groups.investigation;
}
function _temp10($_6) {
  return $_6.chat.contextExplorer.groups.agent;
}
function _temp11($_5) {
  return $_5.chat.contextExplorer.groups.playbook;
}
function _temp12($_4) {
  return $_4.chat.contextExplorer.groups.person;
}
function _temp13($_3) {
  return $_3.chat.contextExplorer.groups.business;
}
function _temp14($_2) {
  return $_2.chat.contextExplorer.groups.onboarding;
}
function _temp15($_1) {
  return $_1.chat.contextExplorer.groups.artifact;
}
function _temp16($_0) {
  return $_0.chat.contextExplorer.groups.appearance;
}
function _temp17(group_3) {
  switch (group_3) {
    case "investigation":
      {
        return <IcoFileText16 color="tertiary" />;
      }
    case "agent":
      {
        return <IcoRobot16 color="tertiary" />;
      }
    case "playbook":
      {
        return <IcoBook16 color="tertiary" />;
      }
    case "person":
      {
        return <IcoUser16 color="tertiary" />;
      }
    case "business":
      {
        return <IcoBuilding16 color="tertiary" />;
      }
    case "onboarding":
      {
        return <IcoShield16 color="tertiary" />;
      }
    case "artifact":
      {
        return <IcoFileText16 color="tertiary" />;
      }
    case "appearance":
      {
        return <IcoPaint16 color="tertiary" />;
      }
  }
}
function _temp18(a, b) {
  return RESOURCE_GROUP_ORDER[a] - RESOURCE_GROUP_ORDER[b];
}
function _temp19($_8) {
  return $_8.chat.contextExplorer.searchPlaceholder;
}
function _temp20($_9) {
  return $_9.chat.contextExplorer.clearSearch;
}
function _temp21($_10) {
  return $_10.chat.contextExplorer.closeSearch;
}
function _temp22($_11) {
  return $_11.chat.contextExplorer.search;
}
function _temp23($_12) {
  return $_12.chat.contextExplorer.close;
}
function _temp24($_13) {
  return $_13.chat.contextExplorer.open;
}
function _temp25($_14) {
  return $_14.chat.contextExplorer.reveal;
}
function _temp26($_15) {
  return $_15.chat.contextExplorer.noMatches;
}
function _temp27($_16) {
  return $_16.chat.contextExplorer.empty;
}